import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FaArrowLeft, FaChartLine } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";

const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currency.format(Number(value) || 0);

export default function SchoolRevenueDetails() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState("School Revenue");
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const fetchDetails = async () => {
      try {
        const [subscriptionResponse, schoolsResponse] = await Promise.all([
          api.get(`/super_admin/subscriptions/list?school_id=${schoolId}`),
          api.get("/super_admin/school/list"),
        ]);
        const subscriptions = subscriptionResponse.data?.data || [];
        const school = (schoolsResponse.data?.data || []).find(
          (item) => String(item.id) === String(schoolId)
        );
        const byMonth = {};

        subscriptions
          .filter((subscription) => String(subscription.status).toLowerCase() === "paid")
          .forEach((subscription) => {
            const month = subscription.month || "Unknown";
            if (!byMonth[month]) byMonth[month] = { month, total: 0, payments: 0 };
            byMonth[month].total += Number(subscription.amount) || 0;
            byMonth[month].payments += 1;
          });

        if (active) {
          setSchoolName(school?.name || "School Revenue");
          setMonthlyRows(
            Object.values(byMonth).sort((first, second) => second.month.localeCompare(first.month))
          );
        }
      } catch (requestError) {
        console.error("Fetch school revenue details error:", requestError);
        if (active) setError("Unable to load monthly revenue.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchDetails();
    return () => {
      active = false;
    };
  }, [schoolId]);

  const totalRevenue = useMemo(
    () => monthlyRows.reduce((sum, row) => sum + row.total, 0),
    [monthlyRows]
  );

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 16px 40px rgba(16,42,67,0.1)", background: "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(239,250,250,0.92))" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 44, height: 44, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 1.5, color: "#fff", background: "linear-gradient(135deg, #2563eb, #0891b2)" }}>
              <FaChartLine size={22} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#102a43", fontWeight: 900, fontSize: { xs: 21, md: 26 }, overflowWrap: "anywhere" }}>{schoolName}</Typography>
              <Typography sx={{ color: "#607086", mt: 0.35, fontWeight: 600 }}>Monthly paid revenue</Typography>
            </Box>
          </Box>
          <Button variant="outlined" startIcon={<FaArrowLeft />} onClick={() => navigate("/super_admin/revenue")} sx={{ flexShrink: 0, borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}>Back</Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#0891b2" }} /></Box>
        ) : (
          <>
            <Paper sx={{ display: "inline-flex", flexDirection: "column", p: 2, mb: 3, minWidth: 210, borderRadius: 2, border: "1px solid rgba(15,118,110,0.16)", background: "rgba(15,118,110,0.07)" }}>
              <Typography sx={{ color: "#607086", fontSize: 13, fontWeight: 700 }}>Total Paid Revenue</Typography>
              <Typography sx={{ color: "#0f766e", fontSize: 26, fontWeight: 900 }}>{formatCurrency(totalRevenue)}</Typography>
            </Paper>
            <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto", border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 12px 28px rgba(16,42,67,0.08)" }}>
              <Table sx={{ minWidth: 520 }}>
                <TableHead sx={{ bgcolor: "#123a5a" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Month</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Paid Transactions</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>Money Collected</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyRows.map((row, index) => (
                    <TableRow key={row.month} hover sx={{ backgroundColor: index % 2 ? "#f7fafc" : "#fff" }}>
                      <TableCell sx={{ color: "#172033", fontWeight: 800 }}>{row.month}</TableCell>
                      <TableCell>{row.payments}</TableCell>
                      <TableCell align="right" sx={{ color: "#0f766e", fontWeight: 900 }}>{formatCurrency(row.total)}</TableCell>
                    </TableRow>
                  ))}
                  {!monthlyRows.length && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5 }}>No paid revenue found.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Card>
    </Box>
  );
}
